import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Stack,
  TextField,
  Typography,
  FormControl,
  Snackbar,
  Alert,
  FormLabel,
  Tooltip,
  IconButton,
} from "@mui/material";
import FmdBadIcon from "@mui/icons-material/FmdBad";

//共通コンポーネント
import {
  MainColorButton,
  ToggleButtons,
  Loading,
  Header,
  Pulldown,
  DateSelector,
} from "../../components";
// パス
import { ERROR } from "../../routes/path";
// 定数
import { CUSTOMER_FAMILY_INFO_INDEX_AND_NAME } from "../../common/constants";
// API
import {
  setFamilyInformation,
  getCustomerInfo,
  getSystemConstants,
} from "../../server/axios";
//REDUX
import store from "../../store";
import {
  setExclusionErrorFlag,
  setFamiliesData,
  setSystemConstants,
} from "../../store/actions/prescription";
import { closeKeyboard } from "../../utils/closeKeyboard";

/**
 * 利用者家族情報登録
 * @returns
 */
const FamilyAdder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const endY = useRef(0);
  //続柄
  const [relationships, setRelationships] = useState([]);
  //性別
  const [genderArray, setGenderArray] = useState([]);
  //性別helper表示非表示設定
  const [genderOpen, setGenderOpen] = useState(false);
  const [genderClosed, setGenderClosed] = useState(false);
  //減免フラグ
  const [exemptionArray] = useState([
    { id: "1", label: "あり" },
    { id: "2", label: "なし" },
  ]);
  useEffect(() => {
    if (
      !location.state ||
      !location.state?.siteId ||
      !location.state?.customerId
    ) {
      navigate(ERROR);
    }

    // 性別とその他の続柄の選択肢を取得と設定
    const selectSystemConstants = (state) => state.prescription.systemConstants;
    const systemConstants = selectSystemConstants(store.getState());
    if (systemConstants) {
      setRelationships(
        systemConstants
          .filter((r) => r.ConstCd === "relationship_class")
          .map((s) => ({
            id: s.ConstBranchCd,
            value: s.ConstString,
          }))
      );
      setGenderArray(
        systemConstants
          .filter((r) => r.ConstCd === "sex_class" && r.StringSub1 !== "none")
          .map((s) => ({
            id: s.ConstBranchCd,
            label: s.ConstString,
          }))
      );
    } else {
      setLoadingOpen(true);
      getSystemConstants({ isPharmacy: true }).then((res) => {
        if (res.data) {
          store.dispatch(setSystemConstants(res.data));
          setRelationships(
            res.data
              .filter((r) => r.ConstCd === "relationship_class")
              .map((s) => ({
                id: s.ConstBranchCd,
                value: s.ConstString,
              }))
          );
          setGenderArray(
            res.data
              .filter(
                (r) => r.ConstCd === "sex_class" && r.StringSub1 !== "none"
              )
              .map((s) => ({
                id: s.ConstBranchCd,
                label: s.ConstString,
              }))
          );
        } else {
          navigate(ERROR);
        }
        setLoadingOpen(false);
      });
    }
  }, []);

  const { siteId, customerId, updateDatetime, familyId, samePersonCheckArray } =
    location.state;

  let isEditMode = false;
  if (updateDatetime) {
    //編集モード
    isEditMode = true;
  }

  //姓
  const [lastName, setLastName] = useState(location.state?.lastName || "");
  //名
  const [firstName, setFirstName] = useState(location.state?.firstName || "");
  //姓かな
  const [lastNameKana, setLastNameKana] = useState(
    location.state?.lastNameKana || ""
  );
  //名かな
  const [firstNameKana, setFirstNameKana] = useState(
    location.state?.firstNameKana || ""
  );
  //性別
  const [gender, setGender] = useState(location.state?.gender || "");
  //ToggleButtons処理
  const handleGenderAlignment = (_, newAlignment) => {
    setGender(newAlignment);
  };
  //生年月日
  const [birthday, setBirthday] = useState(location.state?.birthday || null);
  //減免フラグToggleButtons処理
  const handleExemptionFlg = (_, exemption) => {
    setExemptionFlg(exemption === "1");
  };
  //減免フラグ
  const [exemptionFlg, setExemptionFlg] = useState(
    location.state?.exemptionFlg
  );
  //利用者との続柄
  const [relationshipClass, setRelationshipClass] = useState(
    location.state?.relationshipClass || ""
  );
  //その他の続柄
  const [relationshipEtc, setRelationshipEtc] = useState(
    location.state?.relationshipEtc || ""
  );

  //Loadingコンポネント表示非表示state
  const [loadingOpen, setLoadingOpen] = useState(false);
  //エラーメッセージ表示
  const [openSnackbar, setOpenSnackbar] = useState({
    status: false,
    message: "",
  });

  const handleSnackbarClose = () => {
    setOpenSnackbar({
      status: false,
      message: "",
    });
  };

  const uploadFamilyInfo = () => {
    if (!samePersonCheckArray || samePersonCheckArray.length < 1) {
      // 本人が既に存在する為、チェックarrayは必ずlengthが1以上になる
      navigate(ERROR);
    } else {
      // 本人も含めて、同姓同名と同じ生年月日の家族追加をチェック
      if (
        samePersonCheckArray.find(
          (s) =>
            s.lastName === lastName &&
            s.firstName === firstName &&
            s.birthday === birthday
        )
      ) {
        setOpenSnackbar({
          status: true,
          message: "名前と生年月日が同一の家族は追加できません。",
        });
        return;
      }
    }

    // 必須入力チェック;
    const emptyCustomerInfo = [
      lastName,
      firstName,
      lastNameKana,
      firstNameKana,
      gender,
      birthday,
      relationshipClass,
      exemptionFlg,
    ]
      .map((info, index) => {
        if (typeof info === "boolean") {
          return info === null
            ? CUSTOMER_FAMILY_INFO_INDEX_AND_NAME[index]
            : undefined;
        } else {
          return !info ? CUSTOMER_FAMILY_INFO_INDEX_AND_NAME[index] : undefined;
        }
      })
      .filter((index) => index !== undefined);
    //その他の続柄必須入力チェック
    if (relationshipClass === "99" && !relationshipEtc)
      emptyCustomerInfo.push("その他の続柄");
    if (emptyCustomerInfo.length > 0) {
      setOpenSnackbar({
        status: true,
        message: (
          <>
            以下の必須項目を入力してください。
            {emptyCustomerInfo.map((info, index) => (
              <div key={index} style={{ display: "inline" }}>
                <br />
                <strong>{info}</strong>
              </div>
            ))}
          </>
        ),
      });
      return;
    }

    //その他の続柄：20桁数チェック
    if (relationshipEtc && relationshipEtc.split("").length > 20) {
      setOpenSnackbar({
        status: true,
        message: "その他の続柄は20桁以内に入力してください。",
      });
      return;
    }

    setLoadingOpen(true);
    setFamilyInformation({
      lastName,
      firstName,
      lastNameKana,
      firstNameKana,
      gender,
      birthday,
      exemptionFlg,
      relationshipClass,
      relationshipEtc,
      customerId: customerId,
      siteId: siteId,
      familyId,
      updateDatetime,
    }).then((res) => {
      if (res.data.result === -1) {
        //排他エラー処理
        store.dispatch(setExclusionErrorFlag(true));
        setLoadingOpen(false);
        navigate(-1);
      } else {
        const selectLineInfo = (state) => state.prescription.lineInfo;
        const lineInfo = selectLineInfo(store.getState());
        getCustomerInfo({ lineId: lineInfo.lineId }).then((res) => {
          store.dispatch(setExclusionErrorFlag(false));
          store.dispatch(setFamiliesData(res.data.families));
          setLoadingOpen(false);
          navigate(-1);
        });
      }
    });
  };

  return (
    <div
      onTouchStartCapture={() => {
        endY.current = 0;
      }}
      onTouchMoveCapture={(e) => {
        endY.current = e.touches[0].clientY;
      }}
      onTouchEndCapture={(e) => {
        if (endY.current !== 0) {
          e.stopPropagation();
        } else {
          if (e.target.id !== "tooltipAreaGender" && genderOpen === true) {
            setGenderOpen(false);
            setGenderClosed(true);
          }
        }
      }}
    >
      <Loading open={loadingOpen} />
      <Header
        title={isEditMode ? "利用者家族情報変更" : "利用者家族情報登録"}
        withBackButton={true}
      />
      <Snackbar
        open={openSnackbar.status}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={handleSnackbarClose}
        sx={{ mt: "8vh" }}
      >
        <Alert
          severity="error"
          sx={{ width: "100%" }}
          onClose={handleSnackbarClose}
        >
          {openSnackbar.message}
        </Alert>
      </Snackbar>
      {relationships &&
        relationships.length > 0 &&
        genderArray &&
        genderArray.length > 0 && (
          <Stack
            spacing="8%"
            marginTop="9vh"
            width="95%"
            marginLeft="auto"
            marginRight="auto"
            sx={{ height: "76vh", overflowY: "scroll" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box width="19%">
                <Typography>名前</Typography>
              </Box>
              <Stack width="81%" direction="row" alignItems="center">
                <Typography color="red" width="15%">
                  必須
                </Typography>
                <Stack
                  width="85%"
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <TextField
                    width="60%"
                    variant="outlined"
                    size="small"
                    placeholder="(姓)"
                    onChange={(event) => {
                      setLastName(event.target.value);
                    }}
                    onKeyDown={(event) => closeKeyboard(event)}
                    value={lastName}
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="(名)"
                    onChange={(event) => {
                      setFirstName(event.target.value);
                    }}
                    onKeyDown={(event) => closeKeyboard(event)}
                    value={firstName}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box width="19%">
                <Typography>かな</Typography>
              </Box>
              <Stack width="81%" direction="row" alignItems="center">
                <Typography color="red" width="15%">
                  必須
                </Typography>
                <Stack
                  width="85%"
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <TextField
                    width="60%"
                    variant="outlined"
                    size="small"
                    placeholder="(姓)"
                    onChange={(event) => {
                      setLastNameKana(event.target.value);
                    }}
                    onKeyDown={(event) => closeKeyboard(event)}
                    value={lastNameKana}
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="(名)"
                    onChange={(event) => {
                      setFirstNameKana(event.target.value);
                    }}
                    onKeyDown={(event) => closeKeyboard(event)}
                    value={firstNameKana}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box width="19%">
                <Typography style={{ fontSize: "0.8rem" }}>
                  戸籍の性別
                </Typography>
              </Box>
              <Box width="81%">
                <Stack direction="row" alignItems="center">
                  <Typography color="red" width="15%">
                    必須
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <FormControl fullWidth>
                      <ToggleButtons
                        items={genderArray}
                        value={gender}
                        setValue={handleGenderAlignment}
                      />
                    </FormControl>
                    <Tooltip
                      open={genderOpen}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      PopperProps={{
                        disablePortal: true,
                        sx: { maxWidth: "85vw" },
                      }}
                      title={
                        <Box id="tooltipAreaGender">
                          <Typography id="tooltipAreaGender">
                            －性別について－
                            <br />
                            薬機法上、戸籍に登録された性別の入力をお願いしております。
                            申し訳ございませんが、ジェンダーの方でも戸籍上の性別を入力してください。
                          </Typography>
                        </Box>
                      }
                    >
                      <IconButton
                        id="tooltipButtonGender"
                        onTouchEnd={() => {
                          if (!genderClosed) {
                            setGenderOpen(!genderOpen);
                          }
                          setGenderClosed(false);
                        }}
                      >
                        <FmdBadIcon id="tooltipButtonGender" color="error" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box width="19%">
                <Typography style={{ fontSize: "0.8rem" }}>
                  戸籍の
                  <span style={{ display: "block" }}>生年月日</span>
                </Typography>
              </Box>
              <Box width="81%">
                <Stack direction="row" alignItems="center">
                  <Typography color="red" width="15%">
                    必須
                  </Typography>
                  <DateSelector
                    birthday={location.state?.birthday}
                    setBirthday={(b) => setBirthday(b)}
                  />
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box width="35%">
                <Typography>利用者との続柄</Typography>
              </Box>
              <Box width="61%">
                <Stack direction="row" alignItems="center">
                  <Typography color="red" width="15%" marginRight="3%">
                    必須
                  </Typography>
                  <Box width="50%">
                    <FormControl fullWidth>
                      <Pulldown
                        size="small"
                        withLabel={false}
                        withMenuProps={true}
                        withEmptyMenu={true}
                        items={relationships}
                        value={relationshipClass}
                        setValue={(e) => setRelationshipClass(e)}
                      />
                    </FormControl>
                  </Box>
                </Stack>
              </Box>
            </Stack>
            {relationshipClass === "99" && (
              <Stack direction="row" spacing={1}>
                <Box width="35%" />
                <Box width="65%">
                  <FormControl fullWidth>
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder="続柄を入力してください"
                      onChange={(event) => {
                        setRelationshipEtc(event.target.value);
                      }}
                      value={relationshipEtc}
                    />
                  </FormControl>
                </Box>
              </Stack>
            )}
            <FormControl>
              <FormLabel>
                <Typography color="black" display="inline">
                  自己負担の減免等を証明する書類
                </Typography>
                <Typography color="red" display="inline" marginLeft="3%">
                  必須
                </Typography>
                <br />
                <Typography color="black" display="inline">
                  （乳幼児医療証などの各種受給資格証）
                </Typography>
              </FormLabel>
              <Box sx={{ marginLeft: "35%" }}>
                <FormControl fullWidth>
                  <ToggleButtons
                    items={exemptionArray}
                    value={!!exemptionFlg ? "1" : "2"}
                    setValue={handleExemptionFlg}
                  />
                </FormControl>
              </Box>
            </FormControl>
          </Stack>
        )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          flexDirection: "row",
          flexWrap: "nowrap",
          position: "fixed",
          width: "100%",
        }}
      >
        <MainColorButton
          label="キャンセル"
          style={{
            height: "6vh",
            fontSize: "120%",
            width: "calc( ( 100vw - 30px ) / 2 )",
            paddingX: 0,
          }}
          onClick={() => {
            store.dispatch(setExclusionErrorFlag(false));
            navigate(-1);
          }}
        />
        <MainColorButton
          label="登録"
          style={{
            height: "6vh",
            fontSize: "120%",
            width: "calc( ( 100vw - 30px ) / 2 )",
            paddingX: 0,
          }}
          onClick={uploadFamilyInfo}
        />
      </Box>
    </div>
  );
};

export default FamilyAdder;
